'use client';

import { extractGifPreviewFrame, readGifMetadata, type GifMetadata } from '@/lib/browser-gif-tools';
import { clamp } from '@/lib/browser-image-tools';
import { useEffect, useState } from 'react';

export type LoadedGifSource = {
  file: File;
  previewUrl: string;
  metadata: GifMetadata;
  previewFrameUrl: string | null;
};

export function isGifFile(file: File) {
  return file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif');
}

export function formatDurationMs(durationMs: number) {
  const totalSeconds = Math.max(0, Math.round(durationMs / 10) / 100);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    return `${minutes}:${seconds.toFixed(2).padStart(5, '0')}`;
  }

  return `${seconds.toFixed(2)}s`;
}

export function formatLoopLabel(loopCount: number | null) {
  if (loopCount === null) {
    return 'Not detected';
  }

  return loopCount === 0 ? 'Infinite' : `${loopCount} loops`;
}

export function getLoopOutputValue(loopCount: number | null) {
  if (loopCount === null) {
    return 0;
  }

  return clamp(Math.round(loopCount), 0, 999);
}

export const acceptedVideoMimeTypes =
  'video/mp4,video/webm,video/quicktime,video/x-m4v,video/mpeg,video/avi,video/*,.mkv,.ts,.mts,.avi,.wmv,.flv,.vob,.3gp';

export function useLoadedGifSource(includePreviewFrame = false) {
  const [source, setSource] = useState<LoadedGifSource | null>(null);

  useEffect(() => {
    return () => {
      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }

      if (source?.previewFrameUrl) {
        URL.revokeObjectURL(source.previewFrameUrl);
      }
    };
  }, [source]);

  async function loadSource(file: File) {
    if (!isGifFile(file)) {
      throw new Error('Choose one GIF file for this tool.');
    }

    const [metadata, previewFrameBlob] = await Promise.all([
      readGifMetadata(file),
      includePreviewFrame ? extractGifPreviewFrame(file) : Promise.resolve<Blob | null>(null),
    ]);

    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      if (current?.previewFrameUrl) {
        URL.revokeObjectURL(current.previewFrameUrl);
      }

      return {
        file,
        metadata,
        previewUrl: URL.createObjectURL(file),
        previewFrameUrl: previewFrameBlob ? URL.createObjectURL(previewFrameBlob) : null,
      };
    });
  }

  function clearSource() {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      if (current?.previewFrameUrl) {
        URL.revokeObjectURL(current.previewFrameUrl);
      }

      return null;
    });
  }

  return {
    source,
    loadSource,
    clearSource,
  };
}
